import { useEffect, useState } from "react";
import { fetchCardBinAllowed } from "@connect/data/internal/bin-allowed";
import { log, track } from "@connect/observability";
import type { CardBrand } from "./components/card-fields";

interface UseBinCheckArgs {
  cardBin: string | undefined;
  brand: CardBrand;
  baseUrl: string;
  apiTarget?: string;
}

interface BinResult {
  bin: string;
  notAllowed: boolean;
  cardPaymentType: "credit" | "debit" | null;
  cardIssuingCountry: string | null;
}

const MIN_BIN_LENGTH = 6;
const DEBOUNCE_MS = 300;

export function useBinCheck({ cardBin, brand, baseUrl, apiTarget }: UseBinCheckArgs) {
  const [result, setResult] = useState<BinResult | null>(null);

  const active = !!cardBin && cardBin.length >= MIN_BIN_LENGTH;

  useEffect(() => {
    if (!active || !cardBin) {
      return;
    }
    let ignore = false;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      fetchCardBinAllowed({
        baseUrl,
        cardBin,
        apiTarget,
        signal: controller.signal,
      })
        .then((res) => {
          if (!ignore) {
            setResult({
              bin: cardBin,
              notAllowed: !res.success,
              cardPaymentType: res.cardPaymentType,
              cardIssuingCountry: res.cardIssuingCountry,
            });
          }
        })
        .catch((err: unknown) => {
          if (ignore) {
            return;
          }
          log.warn("connect.add_card BIN allow-list check failed", {
            message: err instanceof Error ? err.message : String(err),
          });
          setResult({
            bin: cardBin,
            notAllowed: false,
            cardPaymentType: null,
            cardIssuingCountry: null,
          });
        });
    }, DEBOUNCE_MS);
    return () => {
      ignore = true;
      controller.abort();
      clearTimeout(timer);
    };
  }, [active, cardBin, baseUrl, apiTarget]);

  // Only trust a result that still matches the current BIN, so a stale in-flight
  // response for a previous number can't block the one now in the field.
  const matches = !!result && result.bin === cardBin;
  const blocked = matches && result.notAllowed;
  // Issuing card type / country for the current BIN, or null until a matching check resolves.
  const cardPaymentType = matches ? result.cardPaymentType : null;
  const cardIssuingCountry = matches ? result.cardIssuingCountry : null;

  // A check is warranted for the current BIN but hasn't resolved yet (debounce +
  // network window). Callers should block submit while pending so a fast submit
  // can't slip through before a disallowed BIN flips `blocked`.
  const pending = active && !matches;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (blocked) {
      track.info("connect.add_card.brand_not_accepted_viewed", { brand });
    }
    // oxlint-disable-next-line react-hooks-js/exhaustive-deps -- fire once on entering the blocked state, not when brand updates
  }, [blocked]);

  return { blocked, pending, cardPaymentType, cardIssuingCountry };
}
